import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useMemo } from 'react';
import type React from 'react';
import { HotTable, HotTableClass } from '@handsontable/react';
import Handsontable from 'handsontable';
// ВАЖНО: порядок импорта CSS критичен — сначала базовые стили, потом тема!
import 'handsontable/styles/handsontable.min.css';
import 'handsontable/styles/ht-theme-main.min.css';

import { initHandsontable } from './initHandsontable';
import { HOT_DEFAULTS } from './hotDefaults';
import { usePaginationPageSize } from './hooks/usePaginationPageSize';
import { useUIConfig } from '../../contexts/UIConfigContext';
import { synchronizeHotLayout } from './synchronizeHotLayout';

initHandsontable();

type SectionCoords = { row: number; col: number };

type CollapsibleColumnsInternals = {
  isEnabled: () => boolean;
  toggleCollapsibleSection: (coords: SectionCoords[], action: 'collapse' | 'expand') => void;
  headerStateManager?: {
    mapNodes: <T>(cb: (node: { isCollapsed?: boolean; headerLevel: number; columnIndex: number }) => T | undefined) => T[];
    levelToRowCoords: (headerLevel: number) => number;
  } | null;
};

/**
 * Разворачивает все свёрнутые группы collapsibleColumns и возвращает функцию,
 * сворачивающую их обратно (по тем же координатам заголовков). Каждая группа
 * сворачивается отдельно: если в новых nestedHeaders её больше нет, HT отклонит
 * только её, а не весь батч.
 */
function expandCollapsedSections(hot: Handsontable): () => void {
  const plugin = hot.getPlugin('collapsibleColumns') as unknown as CollapsibleColumnsInternals | undefined;
  const stateManager = plugin?.headerStateManager;
  if (!plugin?.isEnabled() || !stateManager) return () => {};

  const collapsed = stateManager.mapNodes((node) => (
    node.isCollapsed
      ? { row: stateManager.levelToRowCoords(node.headerLevel), col: node.columnIndex }
      : undefined
  ));
  if (collapsed.length === 0) return () => {};

  plugin.toggleCollapsibleSection(collapsed, 'expand');
  return () => {
    if (hot.isDestroyed || !plugin.isEnabled()) return;
    for (const coords of collapsed) plugin.toggleCollapsibleSection([coords], 'collapse');
  };
}

export interface HandsontableWrapperRef {
  getHotInstance: () => Handsontable | null;
  updateData: (newData: (string | number | boolean | null)[][]) => void;
  refresh: () => void;
}

export interface HandsontableWrapperSimpleProps {
  // ── Core ──────────────────────────────────────────────────────────────
  data: (string | number | boolean | null)[][] | Array<Record<string, unknown>>;
  columns?: Handsontable.ColumnSettings[];
  colHeaders?: boolean | string[];
  rowHeaders?: boolean | string[];
  nestedHeaders?: Handsontable.GridSettings['nestedHeaders'];
  tableId?: string;

  // ── Часто переопределяемые настройки ──────────────────────────────────
  readOnly?: boolean;
  cells?: Handsontable.GridSettings['cells'];
  contextMenu?: boolean | string[] | object;
  height?: number | 'auto';
  colWidths?: number | string | number[] | string[] | ((index: number) => number | string);
  hiddenColumns?: Handsontable.GridSettings['hiddenColumns'];
  stretchH?: 'none' | 'last' | 'all';
  pagination?: Handsontable.GridSettings['pagination'];

  // ── Callbacks ─────────────────────────────────────────────────────────
  onAfterChange?: (changes: (Handsontable.CellChange | null)[] | null, source: Handsontable.ChangeSource) => void;
  onBeforeChange?: (changes: (Handsontable.CellChange | null)[], source: Handsontable.ChangeSource) => boolean | void;
  onAfterSelection?: (r: number, c: number, r2: number, c2: number, preventScrolling: object, selectionLayerLevel: number) => void;
  beforePageSizeChange?: Handsontable.GridSettings['beforePageSizeChange'];
  beforePageChange?: Handsontable.GridSettings['beforePageChange'];
  afterInit?: Handsontable.GridSettings['afterInit'];
  afterCreateRow?: Handsontable.GridSettings['afterCreateRow'];
  afterRemoveRow?: Handsontable.GridSettings['afterRemoveRow'];
  /** Вызывается при изменении фильтров (true = есть активные фильтры) */
  onFiltersChanged?: (hasActiveFilters: boolean) => void;

  /**
   * Escape-hatch: любые настройки Handsontable.GridSettings,
   * которые не вынесены в отдельные пропсы.
   *
   * Мержится ПОВЕРХ defaults — пользовательские значения побеждают.
   * Потребитель отвечает за мемоизацию этого объекта (useMemo).
   */
  hotSettings?: Partial<Handsontable.GridSettings>;

  // ── Config-driven header colors ───────────────────────────────────────
  /** Секция UIConfig для цветов заголовков (напр. 'sales_stage2') */
  configSection?: string;
  /** Ключ заголовков в UIConfig для нижнего уровня (напр. 'column_headers') */
  configHeaderKey?: string;
  /** Ключ заголовков в UIConfig для верхнего уровня nested headers */
  configNestedHeaderKey?: string;
  /** Ключ заголовков в UIConfig для повторяющихся sub-headers (напр. 'bg_sub_headers') */
  configSubHeaderKey?: string;

  // ── React ─────────────────────────────────────────────────────────────
  className?: string;
  style?: React.CSSProperties;
  id?: string;
}

/**
 * Thin wrapper вокруг HotTable.
 *
 * Ответственности:
 * - Инициализация HT (numbro, i18n, modules)
 * - Объединение defaults + explicit props + hotSettings
 * - Pagination pageSize memory (через хук)
 * - Tooltip'ы на заголовках
 * - Imperative ref для getHotInstance/updateData/refresh
 *
 * НЕ содержит: title/toolbar/ResizableTableContainer — это ответственность
 * TrackedTable или прямого потребителя.
 */
export const HandsontableWrapperSimple = memo(forwardRef<HandsontableWrapperRef, HandsontableWrapperSimpleProps>(({
  data,
  columns,
  colHeaders = true,
  rowHeaders = true,
  nestedHeaders,
  tableId,
  readOnly = false,
  cells,
  contextMenu = true,
  height = 'auto',
  colWidths,
  hiddenColumns,
  stretchH: stretchHProp,
  pagination,
  onAfterChange,
  onBeforeChange,
  onAfterSelection,
  beforePageSizeChange,
  beforePageChange,
  afterInit,
  afterCreateRow,
  afterRemoveRow,
  onFiltersChanged,
  configSection,
  configHeaderKey,
  configNestedHeaderKey,
  configSubHeaderKey,
  hotSettings,
  className,
  style,
  id,
}, ref) => {
  const hotTableRef = useRef<HotTableClass>(null);
  const layoutSyncFrameRef = useRef<number | null>(null);
  const layoutSyncFollowupFrameRef = useRef<number | null>(null);

  // afterFilter callback — через ref, чтобы не пересоздавать settings
  const onFiltersChangedRef = useRef(onFiltersChanged);
  onFiltersChangedRef.current = onFiltersChanged;

  // Config-driven header colors: если указаны configSection + configHeaderKey,
  // цвета из UIConfig применяются поверх пользовательского afterGetColHeader.
  const { getHeaderColors } = useUIConfig();
  // Цвета для нижнего уровня заголовков (реальные колонки)
  const bottomHeaderColors = useMemo(
    () => (configSection && configHeaderKey)
      ? getHeaderColors(configSection, configHeaderKey)
      : new Map<number, string>(),
    [getHeaderColors, configSection, configHeaderKey],
  );
  // Цвета для верхнего уровня nested headers (группировка)
  const topHeaderColors = useMemo(
    () => (configSection && configNestedHeaderKey)
      ? getHeaderColors(configSection, configNestedHeaderKey)
      : new Map<number, string>(),
    [getHeaderColors, configSection, configNestedHeaderKey],
  );
  // Цвета для повторяющихся sub-headers (напр. 3 колонки на каждую БГ)
  const subHeaderColors = useMemo(
    () => (configSection && configSubHeaderKey)
      ? getHeaderColors(configSection, configSubHeaderKey)
      : new Map<number, string>(),
    [getHeaderColors, configSection, configSubHeaderKey],
  );
  const hasNestedHeaders = !!nestedHeaders;

  // Маппинг columnIndex → groupIndex для top-level nested headers.
  // ВАЖНО: индексируем ТОЛЬКО именованные группы — пустые плейсхолдеры
  // (`{label:'', colspan:N}`) пропускаются, чтобы они не «съедали» индексы
  // в configNestedHeaderKey. Так конфиг описывает только реальные группы,
  // и админу не приходится держать в нём пустые строки-плейсхолдеры.
  // Пример: nestedHeaders[0] = [{label:'', colspan:8}, {label:'ЯНВАРЬ', colspan:2}, ...]
  // → Map: {8→0 (ЯНВАРЬ), 10→1 (ФЕВРАЛЬ), ...}
  // Пример: nestedHeaders[0] = [{label:'Показатели', colspan:11}, {label:'БГ ДВФО', colspan:3}, ...]
  // → Map: {0→0 (Показатели), 11→1 (БГ ДВФО), ...}
  const colToGroupIndex = useMemo(() => {
    const map = new Map<number, number>();
    if (!nestedHeaders || !Array.isArray(nestedHeaders) || nestedHeaders.length === 0) return map;
    const topRow = nestedHeaders[0] as Array<string | { label: string; colspan: number }>;
    if (!Array.isArray(topRow)) return map;
    let colOffset = 0;
    let labeledIdx = 0;
    topRow.forEach((item) => {
      const label = typeof item === 'string' ? item : item.label;
      const span = typeof item === 'string' ? 1 : (item.colspan || 1);
      if (label && label.trim()) {
        map.set(colOffset, labeledIdx);
        labeledIdx++;
      }
      colOffset += span;
    });
    return map;
  }, [nestedHeaders]);

  // Геометрия повторяющихся sub-header групп: colspan первой группы определяет
  // ширину «базовой» секции (где НЕ применяется sub-header окраска), colspan
  // второй группы — ширину повторяющейся группы. Считаем напрямую из исходной
  // структуры nestedHeaders[0], независимо от того, какая группа именована,
  // а какая — плейсхолдер.
  const subHeaderGeometry = useMemo(() => {
    if (!nestedHeaders || !Array.isArray(nestedHeaders) || nestedHeaders.length === 0) return null;
    const topRow = nestedHeaders[0] as Array<string | { label: string; colspan: number }>;
    if (!Array.isArray(topRow) || topRow.length < 2) return null;
    const labelOf = (it: string | { label: string; colspan: number }) =>
      typeof it === 'string' ? it : (it.label ?? '');
    const spanOf = (it: string | { label: string; colspan: number }) =>
      typeof it === 'string' ? 1 : (it.colspan || 1);

    // Базовая секция — это ведущие ПУСТЫЕ плейсхолдеры. Их может быть несколько:
    // builder разбивает базу по границе фиксированных колонок (fixedColumnsStart),
    // напр. [{'',5},{'',6},{ЯНВАРЬ,4},…] → база = 5+6 = 11, группа = 4.
    // Первая ИМЕНОВАННАЯ группа после плейсхолдеров задаёт colsPerGroup.
    let i = 0;
    let baseColCount = 0;
    while (i < topRow.length && !labelOf(topRow[i]).trim()) {
      baseColCount += spanOf(topRow[i]);
      i++;
    }
    if (i === 0) {
      // Первый элемент именован (напр. Stage3 «Показатели») — он и есть база.
      return { baseColCount: spanOf(topRow[0]), colsPerGroup: spanOf(topRow[1]) };
    }
    const colsPerGroup = i < topRow.length ? spanOf(topRow[i]) : 0;
    return { baseColCount, colsPerGroup };
  }, [nestedHeaders]);

  // Refs — чтобы afterGetColHeader всегда видел актуальные значения
  // без пересоздания settings (что вызывает тяжёлый updateSettings в HT)
  const bottomColorsRef = useRef(bottomHeaderColors);
  const topColorsRef = useRef(topHeaderColors);
  const subColorsRef = useRef(subHeaderColors);
  const colToGroupIndexRef = useRef(colToGroupIndex);
  const subHeaderGeometryRef = useRef(subHeaderGeometry);
  bottomColorsRef.current = bottomHeaderColors;
  topColorsRef.current = topHeaderColors;
  subColorsRef.current = subHeaderColors;
  colToGroupIndexRef.current = colToGroupIndex;
  subHeaderGeometryRef.current = subHeaderGeometry;

  // Хук для сохранения выбранного пользователем pageSize между ре-рендерами.
  const { resolvedPagination, handleBeforePageSizeChange } = usePaginationPageSize({
    pagination,
    tableId,
    beforePageSizeChange,
  });

  useImperativeHandle(ref, () => ({
    getHotInstance: () => hotTableRef.current?.hotInstance || null,
    updateData: (newData: (string | number | boolean | null)[][]) => {
      hotTableRef.current?.hotInstance?.loadData(newData);
    },
    refresh: () => {
      hotTableRef.current?.hotInstance?.render();
    },
  }), []);

  const syncHotLayout = useCallback(() => {
    if (layoutSyncFrameRef.current !== null) {
      cancelAnimationFrame(layoutSyncFrameRef.current);
    }
    if (layoutSyncFollowupFrameRef.current !== null) {
      cancelAnimationFrame(layoutSyncFollowupFrameRef.current);
      layoutSyncFollowupFrameRef.current = null;
    }

    layoutSyncFrameRef.current = requestAnimationFrame(() => {
      layoutSyncFrameRef.current = null;
      const hot = hotTableRef.current?.hotInstance;
      if (!hot || hot.isDestroyed) return;

      synchronizeHotLayout(hot);

      layoutSyncFollowupFrameRef.current = requestAnimationFrame(() => {
        layoutSyncFollowupFrameRef.current = null;
        const nextHot = hotTableRef.current?.hotInstance;
        if (!nextHot || nextHot.isDestroyed) return;
        synchronizeHotLayout(nextHot);
      });
    });
  }, []);

  useEffect(() => () => {
    if (layoutSyncFrameRef.current !== null) {
      cancelAnimationFrame(layoutSyncFrameRef.current);
    }
    if (layoutSyncFollowupFrameRef.current !== null) {
      cancelAnimationFrame(layoutSyncFollowupFrameRef.current);
    }
  }, []);

  // React-пропы могут поменять число/порядок строк, высоту viewport или набор
  // overlay-колонок. После того как HotTable применит updateSettings(), двумя
  // frame-ами выравниваем master и clones на уже стабилизировавшемся DOM.
  useEffect(() => {
    syncHotLayout();
  }, [
    syncHotLayout,
    data,
    columns,
    colHeaders,
    rowHeaders,
    nestedHeaders,
    hiddenColumns,
    height,
    stretchHProp,
    hotSettings?.fixedColumnsStart,
    hotSettings?.fixedRowsTop,
    hotSettings?.fixedRowsBottom,
    hotSettings?.rowHeights,
  ]);

  // data выносим из settings, чтобы изменение данных не пересоздавало весь объект settings.
  // Примечание: @handsontable/react всё равно мержит ВСЕ пропы (включая data) в updateSettings(),
  // но diff-guard ниже отсекает неизменённые ключи, предотвращая лишнюю переинициализацию плагинов.
  const settings: Handsontable.GridSettings = useMemo(() => ({
    // Defaults
    undoRedo: true,
    width: '100%',
    licenseKey: HOT_DEFAULTS.licenseKey,
    language: HOT_DEFAULTS.language,
    themeName: HOT_DEFAULTS.themeName,
    dropdownMenu: HOT_DEFAULTS.dropdownMenuDefault,
    filters: HOT_DEFAULTS.filtersDefault,
    autoColumnSize: false,
    autoRowSize: false,
    manualColumnResize: true,
    manualRowResize: true,

    // Escape hatch — мержится поверх defaults, но ДО explicit props.
    // Потребитель может передать любые GridSettings, например autoRowSize, autoColumnSize и др.
    ...hotSettings,

    // Explicit props — всегда побеждают
    colHeaders,
    rowHeaders,
    nestedHeaders,
    contextMenu: contextMenu ?? HOT_DEFAULTS.contextMenuDefault,
    readOnly,
    cells,
    columns,
    id: tableId ? `table-${tableId}` : undefined,
    // hiddenColumns: explicit prop побеждает только когда передан явно (не undefined).
    // Иначе undefined перезаписал бы hiddenColumns из hotSettings, отключив плагин.
    ...(hiddenColumns !== undefined ? { hiddenColumns } : {}),
    height: height ?? 'auto',
    colWidths,
    stretchH: stretchHProp ?? 'all',
    // renderAllRows: true когда height='auto' — таблица расширяется под контент,
    // виртуальный рендеринг строк не нужен (и ломает viewport-расчёт в canvas-контексте,
    // приводя к показу 5 строк вместо pageSize=10).
    // С pagination: pageSize строк — ничтожно мало, renderAllRows: true не влияет на перф.
    renderAllRows: hotSettings?.renderAllRows ?? (height === 'auto'),
    pagination: resolvedPagination,
    afterInit,
    afterCreateRow,
    afterRemoveRow,

    // Сброс фильтров: уведомляем потребителя о наличии активных фильтров
    afterFilter: (conditionsStack: { column: number }[]) => {
      onFiltersChangedRef.current?.(conditionsStack.length > 0);
      if (typeof hotSettings?.afterFilter === 'function') {
        (hotSettings.afterFilter as (conditionsStack: { column: number }[]) => void)(conditionsStack);
      }
      syncHotLayout();
    },

    // Эти плагины перестраивают row index map или viewport. При fixed columns
    // master и inline-start clone должны завершить расчёт на одном DOM-frame.
    afterColumnSort: (currentSortConfig, destinationSortConfigs) => {
      if (typeof hotSettings?.afterColumnSort === 'function') {
        hotSettings.afterColumnSort(currentSortConfig, destinationSortConfigs);
      }
      syncHotLayout();
    },
    afterPageChange: (oldPage, newPage) => {
      if (typeof hotSettings?.afterPageChange === 'function') {
        hotSettings.afterPageChange(oldPage, newPage);
      }
      if (oldPage !== newPage) syncHotLayout();
    },
    afterPageSizeChange: (oldPageSize, newPageSize) => {
      if (typeof hotSettings?.afterPageSizeChange === 'function') {
        hotSettings.afterPageSizeChange(oldPageSize, newPageSize);
      }
      if (oldPageSize !== newPageSize) syncHotLayout();
    },
    afterRowResize: (newSize, row, isDoubleClick) => {
      if (typeof hotSettings?.afterRowResize === 'function') {
        hotSettings.afterRowResize(newSize, row, isDoubleClick);
      }
      syncHotLayout();
    },

    // HiddenColumns/CollapsibleColumns меняют column index map и nested header DOM
    // раньше, чем Walkontable успевает пересчитать overlay-геометрию. Особенно
    // заметно при fixedColumnsStart + stretchH: шапка уезжает относительно тела.
    afterHideColumns: (currentHideConfig, destinationHideConfig, actionPossible, stateChanged) => {
      if (typeof hotSettings?.afterHideColumns === 'function') {
        hotSettings.afterHideColumns(currentHideConfig, destinationHideConfig, actionPossible, stateChanged);
      }
      if (actionPossible && stateChanged) syncHotLayout();
    },
    afterUnhideColumns: (currentHideConfig, destinationHideConfig, actionPossible, stateChanged) => {
      if (typeof hotSettings?.afterUnhideColumns === 'function') {
        hotSettings.afterUnhideColumns(currentHideConfig, destinationHideConfig, actionPossible, stateChanged);
      }
      if (actionPossible && stateChanged) syncHotLayout();
    },
    afterColumnCollapse: (currentCollapsedColumns, destinationCollapsedColumns, collapsePossible, successfullyCollapsed) => {
      if (typeof hotSettings?.afterColumnCollapse === 'function') {
        hotSettings.afterColumnCollapse(
          currentCollapsedColumns,
          destinationCollapsedColumns,
          collapsePossible,
          successfullyCollapsed,
        );
      }
      if (collapsePossible && successfullyCollapsed) syncHotLayout();
    },
    afterColumnExpand: (currentCollapsedColumns, destinationCollapsedColumns, expandPossible, successfullyExpanded) => {
      if (typeof hotSettings?.afterColumnExpand === 'function') {
        hotSettings.afterColumnExpand(
          currentCollapsedColumns,
          destinationCollapsedColumns,
          expandPossible,
          successfullyExpanded,
        );
      }
      if (expandPossible && successfullyExpanded) syncHotLayout();
    },

    // Tooltip'ы на заголовках — точечно через afterGetColHeader (O(1) на ячейку).
    // ЗАМЕНЯЕТ тяжёлый addHeaderTooltips() в afterRender, который делал querySelectorAll('thead th')
    // по ВСЕМ overlay'ам на КАЖДЫЙ рендер (включая скролл), вызывая дёрганье таблицы.
    afterGetColHeader: (_column: number, TH: HTMLTableCellElement, headerLevel?: number) => {
      // Tooltip
      const text = TH.textContent?.trim();
      if (text && TH.getAttribute('title') !== text) {
        TH.setAttribute('title', text);
      }
      // Пробрасываем пользовательский afterGetColHeader если есть
      if (typeof hotSettings?.afterGetColHeader === 'function') {
        (hotSettings.afterGetColHeader as (column: number, TH: HTMLTableCellElement) => void)(_column, TH);
      }
      // Config-driven header colors (через ref — всегда актуальные, без пересоздания settings):
      // headerLevel=0 → topmost (nested group: «Показатели», «БГ ДВФО»...)
      // headerLevel=-1 → bottommost (реальные колонки: «ЦПР», «ТН»...)
      // Без nested headers: headerLevel=0 = единственный уровень
      let configColor: string | undefined;
      if (hasNestedHeaders) {
        if (headerLevel === 0) {
          // Top level: _column = реальный col index (0, 11, 14...).
          // colToGroupIndex маппит его в позицию группы (0, 1, 2...) — как в конфиге.
          const groupIndex = colToGroupIndexRef.current.get(_column);
          if (groupIndex !== undefined) {
            configColor = topColorsRef.current.get(groupIndex);
            // Dynamic fallback: если точного цвета нет, а конфиг содержит меньше записей
            // чем реальных групп (dynamic columns) — берём цвет последней записи.
            // Пример: конфиг [{«Основные», null}, {«(менеджеры)», "#9333ea", dynamic}]
            // → groupIndex 2,3,4... получат цвет от index 1 (последний в конфиге).
            if (!configColor && topColorsRef.current.size > 0) {
              const maxConfigIdx = Math.max(...topColorsRef.current.keys());
              if (groupIndex > maxConfigIdx) {
                configColor = topColorsRef.current.get(maxConfigIdx);
              }
            }
          }
        } else if (headerLevel === 1) {
          // Bottom level: сначала точный цвет из configHeaderKey
          configColor = bottomColorsRef.current.get(_column);
          // Если нет — проверяем sub-header цвет (повторяющийся паттерн по группам).
          // Геометрия (baseColCount, colsPerGroup) считывается напрямую из
          // nestedHeaders[0] — корректна и для именованной первой группы (Stage3:
          // «Показатели»), и для пустого плейсхолдера (periodsv2).
          if (!configColor && subColorsRef.current.size > 0 && subHeaderGeometryRef.current) {
            const { baseColCount, colsPerGroup } = subHeaderGeometryRef.current;
            if (_column >= baseColCount && colsPerGroup > 0) {
              const subIdx = (_column - baseColCount) % colsPerGroup;
              configColor = subColorsRef.current.get(subIdx);
            }
          }
        }
      } else {
        configColor = bottomColorsRef.current.get(_column);
      }
      if (configColor) {
        TH.style.backgroundColor = configColor;
        TH.style.color = '#fff';
        TH.style.fontWeight = '500';
      }
    },

    // afterRender — только пользовательский callback (без DOM-мутаций)
    afterRender: hotSettings?.afterRender as Handsontable.GridSettings['afterRender'],
  }), [
    colHeaders,
    rowHeaders,
    nestedHeaders,
    contextMenu,
    readOnly,
    cells,
    columns,
    tableId,
    hiddenColumns,
    height,
    colWidths,
    stretchHProp,
    resolvedPagination,
    afterInit,
    afterCreateRow,
    afterRemoveRow,
    hotSettings,
    hasNestedHeaders,
    syncHotLayout,
  ]);

  // При смене цветов из конфига — перерисовать заголовки без пересоздания settings
  useEffect(() => {
    hotTableRef.current?.hotInstance?.render();
  }, [bottomHeaderColors, topHeaderColors, subHeaderColors]);

  // Актуальные ссылки на settings/data для прайминга diff-guard начальными значениями
  // (effect ниже стартует один раз и читает значения первого рендера).
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const dataPropRef = useRef(data);
  dataPropRef.current = data;

  // ── Diff-guard для updateSettings ────────────────────────────────────
  // @handsontable/react в componentDidUpdate вызывает updateSettings()
  // со ВСЕМИ пропами (data, nestedHeaders, collapsibleColumns, columns…)
  // на каждый React-рендер, даже если значения не изменились.
  // Это приводит к ненужной переинициализации плагинов — в частности,
  // CollapsibleColumns/NestedHeaders теряют синхронизацию после смены data,
  // и свёрнутые месяцы теряют лейблы.
  //
  // Решение: перехватываем updateSettings и передаём в оригинал только те
  // ключи, чья ссылка реально изменилась. Если diff пуст — skip.
  useEffect(() => {
    const hot = hotTableRef.current?.hotInstance;
    if (!hot || hot.isDestroyed) return;

    const original = hot.updateSettings;
    const prev = new Map<string, unknown>();

    // Прайминг prev начальными значениями. Патч ставится в useEffect — ПОСЛЕ
    // componentDidMount, где @handsontable/react уже вызвал updateSettings(init=true)
    // оригиналом. Значит ветка `if (init)` ниже больше никогда не сработает, и без
    // прайминга prev остаётся пустым → ПЕРВЫЙ же не-init вызов (первая смена data —
    // WS/рефетч/фильтр/правка в Динамических ПП/ПЗ) посчитает ВСЕ ключи изменившимися
    // и перешлёт `nestedHeaders`/`columns`. Это рассинхронизирует CollapsibleColumns:
    // тело остаётся свёрнутым, а заголовок группы сбрасывает isCollapsed → месяц
    // «возвращается» в шапке. Прайминг начальными settings+data делает diff корректным
    // с первого вызова, и свёрнутые месяцы переживают смену данных.
    for (const key in settingsRef.current) {
      if (Object.prototype.hasOwnProperty.call(settingsRef.current, key)) {
        prev.set(key, (settingsRef.current as Record<string, unknown>)[key]);
      }
    }
    prev.set('data', dataPropRef.current);

    hot.updateSettings = function (
      this: Handsontable,
      newSettings: Handsontable.GridSettings,
      init?: boolean,
    ) {
      // init=true приходит только из componentDidMount — пропускаем как есть
      if (init) {
        for (const key in newSettings) {
          if (Object.prototype.hasOwnProperty.call(newSettings, key)) {
            prev.set(key, (newSettings as Record<string, unknown>)[key]);
          }
        }
        return original.call(this, newSettings, init);
      }

      const diff: Record<string, unknown> = {};
      let hasDiff = false;

      for (const key in newSettings) {
        if (!Object.prototype.hasOwnProperty.call(newSettings, key)) continue;
        const val = (newSettings as Record<string, unknown>)[key];
        if (val !== prev.get(key)) {
          diff[key] = val;
          hasDiff = true;
        }
        prev.set(key, val);
      }

      if (!hasDiff) return;

      // Смена nestedHeaders (новые подписи из UI Config при его рефетче, смена года)
      // пересобирает дерево заголовков: isCollapsed сбрасывается, а колонки свёрнутых
      // групп остаются скрытыми → шапка расходится с телом (подписи «съезжают»).
      // Разворачиваем свёрнутые группы до применения и сворачиваем их обратно после.
      if ('nestedHeaders' in diff) {
        const restore = expandCollapsedSections(this);
        const result = original.call(this, diff as Handsontable.GridSettings, false);
        restore();
        return result;
      }

      return original.call(this, diff as Handsontable.GridSettings, false);
    };

    return () => {
      if (!hot.isDestroyed) {
        hot.updateSettings = original;
      }
    };
  }, []);

  return (
    <div id={id} className={className} style={style}>
      <HotTable
        ref={hotTableRef}
        data={data}
        settings={settings}
        afterChange={onAfterChange}
        beforeChange={onBeforeChange}
        afterSelection={onAfterSelection}
        beforePageSizeChange={handleBeforePageSizeChange}
        beforePageChange={beforePageChange}
      />
    </div>
  );
}));

HandsontableWrapperSimple.displayName = 'HandsontableWrapperSimple';

export default HandsontableWrapperSimple;
